import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SeoHead from '../../components/feature/SeoHead';

export default function ServicesAdditionnelsPage() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    service: '',
    vehicleBrand: '',
    vehicleModel: '',
    vehicleYear: '',
    message: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  // ✅ Supprimer les erreurs Google Maps de la console
  useEffect(() => {
    const originalError = console.error;
    console.error = (...args) => {
      if (
        typeof args[0] === 'string' && 
        (args[0].includes('Google Maps') || 
         args[0].includes('maps.googleapis') ||
         args[0].includes('InvalidValueError'))
      ) {
        return; // Ignorer les erreurs Google Maps
      }
      originalError.apply(console, args);
    };

    return () => {
      console.error = originalError;
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    console.log('📤 Envoi du formulaire avec les données:', formData);

    // Validation stricte
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.phone || !formData.service) {
      console.error('❌ Champs obligatoires manquants');
      setSubmitStatus('error');
      setIsSubmitting(false);
      return;
    }

    try {
      // Préparer les données pour Readdy Forms
      const formDataToSend = new URLSearchParams({
        prenom: formData.firstName,
        nom: formData.lastName,
        email: formData.email,
        telephone: formData.phone,
        service: formData.service,
        marqueVehicule: formData.vehicleBrand || 'Non renseigné',
        modeleVehicule: formData.vehicleModel || 'Non renseigné',
        annee: formData.vehicleYear || 'Non renseigné',
        details: formData.message || 'Non renseigné',
      });

      console.log('📝 Données envoyées:', Object.fromEntries(formDataToSend));

      // Envoyer à Readdy Forms
      const response = await fetch('https://readdy.ai/api/form/d5rtveutc768qf4bqnt0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSend.toString(),
      });

      console.log('📬 Réponse Readdy Forms:', response.status, response.ok);

      if (response.ok) {
        // Envoyer les emails via Edge Function
        console.log('📧 Envoi des emails...');
        const emailResponse = await fetch(
          `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/send-contact-notification`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
            },
            body: JSON.stringify({
              nom: formData.lastName,
              prenom: formData.firstName,
              email: formData.email,
              telephone: formData.phone,
              service: formData.service,
              marqueVehicule: formData.vehicleBrand || 'Non renseigné',
              modeleVehicule: formData.vehicleModel || 'Non renseigné',
              annee: formData.vehicleYear || 'Non renseigné',
              details: formData.message || 'Non renseigné',
            }),
          }
        );

        console.log('📧 Réponse emails:', emailResponse.status, emailResponse.ok);

        if (emailResponse.ok) {
          const emailResult = await emailResponse.json();
          console.log('✅ Emails envoyés avec succès:', emailResult);
        }

        setSubmitStatus('success');
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          service: '',
          vehicleBrand: '',
          vehicleModel: '',
          vehicleYear: '',
          message: '',
        });
        console.log('✅ Formulaire envoyé avec succès !');
      } else {
        const errorText = await response.text();
        console.error('❌ Erreur Readdy Forms:', errorText);
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('❌ Erreur lors de l\'envoi:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://www.louatiautocenter.ch';

  return (
    <div className="min-h-screen bg-dark-bg">
      <SeoHead
        title="Vitres Teintées, Jantes & Entretien Véhicule Payerne | Louati AUTOCENTER"
        description="Services additionnels automobiles à Payerne : pose de vitres teintées homologuées, achat et montage de jantes alliage, entretien et révision complète. Devis gratuit sous 24h."
        keywords="vitres teintées Payerne, jantes alliage Suisse, entretien véhicule Payerne, révision auto Payerne"
        canonical={`${siteUrl}/services-additionnels`}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "Vitres Teintées, Jantes & Entretien Véhicule Payerne | Louati AUTOCENTER",
          "description": "Services additionnels automobiles à Payerne : vitres teintées, jantes alliage, entretien et révision complète.",
          "url": `${siteUrl}/services-additionnels`,
          "inLanguage": "fr-CH",
          "breadcrumb": {
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Accueil", "item": `${siteUrl}/` },
              { "@type": "ListItem", "position": 2, "name": "Services Additionnels", "item": `${siteUrl}/services-additionnels` }
            ]
          }
        }}
      />
      <Navbar />

      {/* Hero Section */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden mt-20">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/cd646a3de2ef06ce4427461fbaf5977a.jpg"
            alt="Services Additionnels"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/70"></div>
        </div>

        <div className="relative z-10 text-center text-white px-6">
          <h1 className="font-serif text-6xl lg:text-8xl font-bold mb-6">
            Services Additionnels
          </h1>
          <p className="text-xl lg:text-2xl font-light max-w-3xl mx-auto">
            Vitres teintées, jantes et entretien complet de votre véhicule
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24 bg-dark-bg">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-20">
            {/* Vitres Teintées */}
            <div className="bg-dark-card border border-dark-border rounded-2xl p-8 hover:shadow-xl hover:shadow-primary/10 transition-all duration-300">
              <div className="w-16 h-16 flex items-center justify-center bg-primary/20 rounded-xl mb-6">
                <i className="ri-window-line text-3xl text-primary"></i>
              </div>
              <h3 className="text-3xl font-bold text-primary mb-4">
                Vitres Teintées
              </h3>
              <p className="text-gray-400 mb-6 leading-relaxed">
                Installation professionnelle de films teintés homologués pour votre véhicule
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start text-gray-300">
                  <i className="ri-check-line text-primary mr-3 mt-1"></i>
                  <span>Protection UV jusqu'à 99%</span>
                </li>
                <li className="flex items-start text-gray-300">
                  <i className="ri-check-line text-primary mr-3 mt-1"></i>
                  <span>Confidentialité et sécurité</span>
                </li>
                <li className="flex items-start text-gray-300">
                  <i className="ri-check-line text-primary mr-3 mt-1"></i>
                  <span>Réduction de la chaleur</span>
                </li>
                <li className="flex items-start text-gray-300">
                  <i className="ri-check-line text-primary mr-3 mt-1"></i>
                  <span>Homologation légale garantie</span>
                </li>
                <li className="flex items-start text-gray-300">
                  <i className="ri-check-line text-primary mr-3 mt-1"></i>
                  <span>Garantie installation</span>
                </li>
              </ul>
              <div className="bg-dark-bg border border-dark-border rounded-lg p-4">
                <p className="text-sm text-gray-400 mb-2">À partir de</p>
                <p className="text-3xl font-bold text-primary">Sur devis</p>
              </div>
            </div>

            {/* Achat de Jantes */}
            <div className="bg-dark-card border border-dark-border rounded-2xl p-8 hover:shadow-xl hover:shadow-primary/10 transition-all duration-300">
              <div className="w-16 h-16 flex items-center justify-center bg-accent-sky/20 rounded-xl mb-6">
                <i className="ri-steering-2-line text-3xl text-accent-sky"></i>
              </div>
              <h3 className="text-3xl font-bold text-primary mb-4">
                Achat de Jantes
              </h3>
              <p className="text-gray-400 mb-6 leading-relaxed">
                Large sélection de jantes alliage et montage professionnel
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start text-gray-300">
                  <i className="ri-check-line text-accent-sky mr-3 mt-1"></i>
                  <span>Conseil personnalisé</span>
                </li>
                <li className="flex items-start text-gray-300">
                  <i className="ri-check-line text-accent-sky mr-3 mt-1"></i>
                  <span>Grandes marques disponibles</span>
                </li>
                <li className="flex items-start text-gray-300">
                  <i className="ri-check-line text-accent-sky mr-3 mt-1"></i>
                  <span>Montage professionnel inclus</span>
                </li>
                <li className="flex items-start text-gray-300">
                  <i className="ri-check-line text-accent-sky mr-3 mt-1"></i>
                  <span>Équilibrage et géométrie</span>
                </li>
                <li className="flex items-start text-gray-300">
                  <i className="ri-check-line text-accent-sky mr-3 mt-1"></i>
                  <span>Garantie qualité</span>
                </li>
              </ul>
              <div className="bg-dark-bg border border-dark-border rounded-lg p-4">
                <p className="text-sm text-gray-400 mb-2">À partir de</p>
                <p className="text-3xl font-bold text-accent-sky">Sur devis</p>
              </div>
            </div>

            {/* Entretien Véhicule */}
            <div className="bg-dark-card border border-dark-border rounded-2xl p-8 hover:shadow-xl hover:shadow-primary/10 transition-all duration-300">
              <div className="w-16 h-16 flex items-center justify-center bg-accent-gold/20 rounded-xl mb-6">
                <i className="ri-tools-line text-3xl text-accent-gold"></i>
              </div>
              <h3 className="text-3xl font-bold text-primary mb-4">
                Entretien Véhicule
              </h3>
              <p className="text-gray-400 mb-6 leading-relaxed">
                Entretien complet et révision de votre véhicule par des professionnels
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start text-gray-300">
                  <i className="ri-check-line text-accent-gold mr-3 mt-1"></i>
                  <span>Révision complète</span>
                </li>
                <li className="flex items-start text-gray-300">
                  <i className="ri-check-line text-accent-gold mr-3 mt-1"></i>
                  <span>Vidange et changement filtres</span>
                </li>
                <li className="flex items-start text-gray-300">
                  <i className="ri-check-line text-accent-gold mr-3 mt-1"></i>
                  <span>Contrôle technique</span>
                </li>
                <li className="flex items-start text-gray-300">
                  <i className="ri-check-line text-accent-gold mr-3 mt-1"></i>
                  <span>Diagnostic électronique</span>
                </li>
                <li className="flex items-start text-gray-300">
                  <i className="ri-check-line text-accent-gold mr-3 mt-1"></i>
                  <span>Pièces d'origine</span>
                </li>
              </ul>
              <div className="bg-dark-bg border border-dark-border rounded-lg p-4">
                <p className="text-sm text-gray-400 mb-2">À partir de</p>
                <p className="text-3xl font-bold text-accent-gold">Sur devis</p>
              </div>
            </div>
          </div>

          {/* Formulaire de Contact */}
          <div className="max-w-4xl mx-auto">
            <div className="bg-dark-card border border-dark-border rounded-2xl p-8 lg:p-12">
              <div className="text-center mb-10">
                <h2 className="font-serif text-4xl font-bold text-primary mb-4">
                  Demande de Devis
                </h2>
                <p className="text-gray-400 text-lg">
                  Remplissez le formulaire ci-dessous et nous vous recontacterons rapidement
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6" data-readdy-form>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
                      Prénom *
                    </label>
                    <input
                      type="text"
                      id="firstName"
                      name="prenom"
                      value={formData.firstName}
                      onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#14B8A6] focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
                      Nom *
                    </label>
                    <input
                      type="text"
                      id="lastName"
                      name="nom"
                      value={formData.lastName}
                      onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#14B8A6] focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#14B8A6] focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                      Téléphone *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="telephone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#14B8A6] focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="service" className="block text-sm font-medium text-gray-700 mb-2">
                    Service souhaité *
                  </label>
                  <select
                    id="service"
                    name="service"
                    value={formData.service}
                    onChange={(e) => setFormData({ ...formData, service: e.target.value })}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#14B8A6] focus:border-transparent cursor-pointer"
                  >
                    <option value="">Sélectionnez un service</option>
                    <option value="Vitres teintées">Vitres teintées</option>
                    <option value="Jantes et pneus">Jantes et pneus</option>
                    <option value="Entretien complet">Entretien complet</option>
                    <option value="Diagnostic électronique">Diagnostic électronique</option>
                    <option value="Autre">Autre</option>
                  </select>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label htmlFor="vehicleBrand" className="block text-sm font-medium text-gray-700 mb-2">
                      Marque du véhicule
                    </label>
                    <input
                      type="text"
                      id="vehicleBrand"
                      name="marqueVehicule"
                      value={formData.vehicleBrand}
                      onChange={(e) => setFormData({ ...formData, vehicleBrand: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#14B8A6] focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label htmlFor="vehicleModel" className="block text-sm font-medium text-gray-700 mb-2">
                      Modèle
                    </label>
                    <input
                      type="text"
                      id="vehicleModel"
                      name="modeleVehicule"
                      value={formData.vehicleModel}
                      onChange={(e) => setFormData({ ...formData, vehicleModel: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#14B8A6] focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label htmlFor="vehicleYear" className="block text-sm font-medium text-gray-700 mb-2">
                      Année
                    </label>
                    <input
                      type="text"
                      id="vehicleYear"
                      name="annee"
                      value={formData.vehicleYear}
                      onChange={(e) => setFormData({ ...formData, vehicleYear: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#14B8A6] focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                    Détails de votre demande
                  </label>
                  <textarea
                    id="message"
                    name="details"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    rows={4}
                    maxLength={500}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#14B8A6] focus:border-transparent resize-none"
                    placeholder="Décrivez votre besoin en détail..."
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    {formData.message.length}/500 caractères
                  </p>
                </div>

                {submitStatus === 'success' && (
                  <div className="mb-6 p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                    <p className="text-green-400 flex items-center">
                      <i className="ri-check-line mr-2"></i>
                      Votre demande a été envoyée avec succès ! Nous vous recontacterons rapidement.
                    </p>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                    <p className="text-red-400 flex items-center">
                      <i className="ri-error-warning-line mr-2"></i>
                      Une erreur est survenue. Veuillez réessayer ou nous contacter directement.
                    </p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full px-8 py-4 bg-primary text-dark-bg rounded-lg font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center">
                      <i className="ri-loader-4-line animate-spin mr-2"></i>
                      Envoi en cours...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center">
                      <i className="ri-send-plane-line mr-2"></i>
                      Envoyer ma demande
                    </span>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Direct */}
      <section className="py-24 bg-dark-card">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-serif text-4xl font-bold text-primary mb-6">
            Besoin d'informations ?
          </h2>
          <p className="text-xl text-gray-400 mb-8">
            Contactez-nous directement pour toute question
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:+41796840588"
              className="inline-flex items-center justify-center bg-primary text-dark-bg px-8 py-4 rounded-full hover:bg-primary/90 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-line mr-3 text-xl"></i>
              <span className="font-semibold">+41 79 684 05 88</span>
            </a>
            <Link
              to="/"
              className="inline-flex items-center justify-center px-6 py-3 border border-dark-border rounded-lg font-semibold hover:bg-dark-bg transition-colors whitespace-nowrap text-gray-300"
            >
              Retour à l'accueil
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
